
interface ReviewData {
  name: string;
  slug: string;
  description: string;
  pricingTier: string;
  selectedFeatures: string[];
  quotas: {
    maxTransactions: number;
    maxApiCalls: number;
    maxUsers: number;
  };
}

interface Step5ReviewProps {
  data: ReviewData;
  onEdit: (step: number) => void;
}

const PRICING_TIERS: Record<string, { name: string; price: number }> = {
  starter: { name: 'Starter Plan', price: 49 },
  growth: { name: 'Growth Plan', price: 149 },
  enterprise: { name: 'Enterprise Plan', price: 499 },
};

const FEATURE_NAMES: Record<string, string> = {
  payments: 'Credit Card Processing',
  ach_transfers: 'ACH/Bank Transfers',
  digital_wallets: 'Digital Wallets',
  fraud_detection: 'Fraud Detection',
  '3d_secure': '3D Secure Authentication',
  payouts: 'Automated Payouts',
  multi_currency: 'Multi-Currency Support',
};

export function Step5Review({ data, onEdit }: Step5ReviewProps) {
  const tier = PRICING_TIERS[data.pricingTier] || PRICING_TIERS.starter;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review Platform Configuration</h2>
        <p className="mt-2 text-sm text-gray-600">
          Please review your platform configuration before creating.
        </p>
      </div>

      <div className="space-y-4">
        {/* Basic Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            <button
              type="button"
              onClick={() => onEdit(1)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Name:</dt>
              <dd className="font-medium text-gray-900">{data.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Slug:</dt>
              <dd className="font-mono text-gray-900">{data.slug}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">URL:</dt>
              <dd className="font-mono text-gray-700 text-xs">
                {data.slug}.appedge.com
              </dd>
            </div>
            {data.description && (
              <div className="pt-2 border-t border-gray-200">
                <dt className="text-gray-600 mb-1">Description:</dt>
                <dd className="text-gray-700">{data.description}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Pricing & Billing */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Pricing & Billing</h3>
            <button
              type="button"
              onClick={() => onEdit(2)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Tier:</dt>
              <dd className="font-medium text-gray-900">{tier.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Price:</dt>
              <dd className="font-semibold text-gray-900">${tier.price}/month</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">Billing:</dt>
              <dd className="text-gray-900">Monthly</dd>
            </div>
          </dl>
        </div>

        {/* Enabled Features */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Enabled Features ({data.selectedFeatures.length})
            </h3>
            <button
              type="button"
              onClick={() => onEdit(3)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Edit
            </button>
          </div>
          <ul className="space-y-2">
            {data.selectedFeatures.map((featureId) => (
              <li
                key={featureId}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <svg
                  className="w-4 h-4 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                {FEATURE_NAMES[featureId] || featureId}
              </li>
            ))}
          </ul>
          {data.selectedFeatures.length === 0 && (
            <p className="text-sm text-gray-500 italic">No features selected</p>
          )}
        </div>

        {/* Resource Quotas */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Resource Quotas</h3>
            <button
              type="button"
              onClick={() => onEdit(4)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-600">Transactions:</dt>
              <dd className="font-medium text-gray-900">
                {data.quotas.maxTransactions.toLocaleString()}/month
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">API Calls:</dt>
              <dd className="font-medium text-gray-900">
                {data.quotas.maxApiCalls.toLocaleString()}/month
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-600">User Accounts:</dt>
              <dd className="font-medium text-gray-900">
                {data.quotas.maxUsers.toLocaleString()}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Final info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg
            className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-green-900">
            <strong>Ready to create platform</strong>
            <p className="mt-1 text-green-800">
              Your platform will be created with the configuration above. You can modify
              settings after creation from the platform dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
